import { LayoutSegmentCaption } from '@sparemin/api-sdk';
import { isEqual } from 'underscore';
import {
  updateVideoLayoutTimelineByRevisionId,
  updateVideoLayoutTimelineSegment as updateVideoLayoutTimelineSegmentServiceAction,
} from 'redux/middleware/api/media-upload-service/actions';
import { UpdateVideoLayoutTimelineSegmentArgs as UpdateVideoLayoutTimelineSegmentServiceArgs } from 'redux/middleware/api/media-upload-service/types';
import { showError } from 'redux/modules/notification';
import { Dispatch, GetState } from 'redux/types';
import * as types from '../action-types';
import {
  createLayoutSegmentByIdSelector,
  createLayoutSegmentCaptionOverrideSelector,
  createLayoutTimelineSelector,
  createVideoLayoutIdSelector,
  dirtyLayoutIdsSelector,
  getVideoMediaSegmentsSelector,
  lastSplitLayoutErrorMessageSelector,
} from '../selectors';
import { LayoutSegmentPatch } from '../types';
import { saveConfiguration } from './embed';
import { cancelAddingToTrack } from './tracks';
import { updateVideo } from './video';

type UpdateVideoLayoutTimelineSegmentArgs = Omit<
  UpdateVideoLayoutTimelineSegmentServiceArgs,
  'timeline' | 'revisionId' | 'segment'
> & {
  videoId: string;
  /**
   * When updating the timeline segment, the data is read from redux state. Since the
   * update pattern for captions doesn't require it to be part of redux state (part of
   * a modal flow which blocks race conditions), it can be passed in as a separate
   * argument
   */
  caption?: LayoutSegmentCaption;
};

function createUpdatedSegment(
  getState: GetState,
  videoId: string,
  segmentId: string,
  caption?: LayoutSegmentCaption,
) {
  const segment = createLayoutSegmentByIdSelector(
    videoId,
    segmentId,
  )(getState());

  const currentCaption = createLayoutSegmentCaptionOverrideSelector(
    videoId,
    segmentId,
  )(getState());

  return caption === undefined || isEqual(currentCaption, caption)
    ? segment
    : { ...segment, caption };
}

// a wrapper around the API action to inject the current timeline for optimistic
// updates
const updateVideoLayoutTimelineSegment = (
  args: UpdateVideoLayoutTimelineSegmentArgs,
) => (dispatch: Dispatch, getState: GetState) => {
  const { caption: captions, segmentId, videoId, uuid } = args;
  const timeline = createLayoutTimelineSelector(uuid)(getState());

  const segment = createUpdatedSegment(getState, videoId, segmentId, captions);

  return dispatch(
    updateVideoLayoutTimelineSegmentServiceAction({
      uuid,
      timeline,
      segmentId,
      segment,
      revisionId: timeline.revisionId,
    }),
  );
};

export const adjustLayoutBoundary = (
  videoId: string,
  gapIndex: number,
  millis: number,
) => (dispatch: Dispatch, getState: GetState) => {
  const layoutId = createVideoLayoutIdSelector(videoId)(getState());
  const mediaSegments = getVideoMediaSegmentsSelector(videoId)(getState());

  dispatch({
    type: types.EMBED_ADJUST_LAYOUT_BOUNDARY,
    payload: {
      gapIndex,
      layoutId,
      millis,
      mediaSegments,
    },
  });
};

export const splitLayoutSegment = (videoId: string, positionSec: number) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  try {
    const mediaSegments = getVideoMediaSegmentsSelector(videoId)(getState());

    dispatch({
      type: types.EMBED_SPLIT_LAYOUT_SEGMENT,
      payload: {
        videoId,
        positionSec,
        mediaSegments,
      },
    });

    const errorMessage = lastSplitLayoutErrorMessageSelector(getState());

    if (errorMessage) {
      dispatch(
        showError({
          dismissAfterSec: 5,
          message: errorMessage,
        }),
      );
    } else {
      dispatch(saveConfiguration());
    }
  } finally {
    // adding-to-track is set by the AddAssetButton, which calls this split action
    dispatch(cancelAddingToTrack());
  }
};

export const deleteLayoutSegment = (videoId: string, segmentId: string) => (
  dispatch: Dispatch,
) => {
  dispatch({
    type: types.EMBED_DELETE_LAYOUT_SEGMENT,
    payload: {
      videoId,
      segmentId,
    },
  });

  dispatch(saveConfiguration());
};

const saveLayout = (layoutId: string) => async (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const layoutTimeline = createLayoutTimelineSelector(layoutId)(getState());

  await dispatch(updateVideoLayoutTimelineByRevisionId(layoutTimeline));
};

export const saveDirtyLayouts = () => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const dirtyLayoutIds = dirtyLayoutIdsSelector(getState());

  return Promise.all(
    dirtyLayoutIds.map(layoutId => dispatch(saveLayout(layoutId))),
  );
};

export const updateLayoutSegment = (
  videoId: string,
  segmentId: string,
  patch: LayoutSegmentPatch,
) => async (dispatch: Dispatch, getState: GetState) => {
  // 0. gather dependencies
  const layoutId = createVideoLayoutIdSelector(videoId)(getState());

  if (!layoutId) {
    return;
  }

  // 1.  update the video if necessary. the patch should only contain the backfill
  // property if it has changed.
  if (patch.backfill?.enableBlur !== undefined) {
    dispatch(
      updateVideo(videoId, {
        blurredBackground: patch.backfill.enableBlur,
      }),
    );
  }

  // 2. update the layout segment in the embed redux state. we shouldn't mark
  // anything as dirty here because the dirty flag will save the whole layout
  // and we have to use the segment endpoint
  dispatch({
    type: types.EMBED_UPDATE_LAYOUT_SEGMENT_SLOTS,
    payload: {
      layoutId,
      segmentId,
      backfill: patch.backfill,
      slots: patch.slots,
    },
  });

  // 3. update the layout segment in the API
  await dispatch(
    updateVideoLayoutTimelineSegment({
      uuid: layoutId,
      segmentId,
      videoId,
      caption: patch.caption,
    }),
  );

  // 4. save the configuration
  dispatch(saveConfiguration());
};
